/**
 * ArenaSearch — type-ahead arena / venue search.
 * Calls the backend proxy which uses OSM Nominatim (free) or Google Places
 * depending on the admin-configured provider.
 *
 * Props:
 *   teamId      string   — required for API call
 *   name        string   — controlled arena name value
 *   address     string   — controlled arena address value
 *   onSelect    fn({name,address}) — called when user picks a suggestion
 *   onChangeName fn(str) — called on manual name edits
 *   onChangeAddress fn(str) — called on manual address edits
 *   placeholder string   — input placeholder
 *   disabled    bool
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, Search, Loader } from 'lucide-react';
import api from '../../services/api.js';

export default function ArenaSearch({
  teamId, name = '', address = '', onSelect,
  onChangeName, onChangeAddress,
  country = '',
  placeholder = 'Search arena or venue…', disabled = false,
}) {
  const [query,       setQuery]       = useState(name);
  const [suggestions, setSuggestions] = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [open,        setOpen]        = useState(false);
  const debounceRef  = useRef(null);
  const wrapRef      = useRef(null);
  const inputRef     = useRef(null);
  // Track if the name was last set by external prop (not user typing)
  const externalSet  = useRef(false);

  // Sync external name prop changes — but only when the value actually differs
  // and the user isn't actively typing (externalSet guard prevents cursor-jump)
  useEffect(() => {
    if (externalSet.current) { externalSet.current = false; return; }
    setQuery(name);
  }, [name]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2 || !teamId) { setSuggestions([]); setOpen(false); return; }
    setLoading(true);
    try {
      // Pass country when set so Nominatim/Google scope results to the team's
      // region. If empty, backend falls back to team settings.country, then to
      // worldwide search.
      const params = country ? { q, country } : { q };
      const { data } = await api.get(`/teams/${teamId}/arena-search`, { params });
      setSuggestions(data || []);
      setOpen((data || []).length > 0);
    } catch { setSuggestions([]); setOpen(false); }
    setLoading(false);
  }, [teamId, country]);

  const handleNameChange = (e) => {
    const val = e.target.value;
    externalSet.current = true; // we're typing, don't let useEffect clobber
    setQuery(val);
    onChangeName?.(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 350);
  };

  const handleSelect = (suggestion) => {
    externalSet.current = true;
    setQuery(suggestion.name);
    setSuggestions([]);
    setOpen(false);
    onSelect?.(suggestion);
    onChangeName?.(suggestion.name);
    onChangeAddress?.(suggestion.address);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      {/* Name input */}
      <div ref={inputRef} style={{ position: 'relative' }}>
        <Search size={13} style={{
          position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
          color: 'var(--text-2)', pointerEvents: 'none',
        }} />
        <input
          type="text"
          className="input-field"
          style={{ paddingLeft: 30 }}
          value={query}
          placeholder={placeholder}
          disabled={disabled}
          onChange={handleNameChange}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          autoComplete="off"
        />
        {loading && (
          <Loader size={13} style={{
            position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
            color: 'var(--ice)', animation: 'spin 0.8s linear infinite',
          }} />
        )}

        {/* Dropdown — anchored to the name input, not the wrapper */}
        {open && suggestions.length > 0 && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
            background: 'var(--surface)', border: '1px solid var(--ice-a25)',
            borderRadius: 10, overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            marginTop: 4,
            // Mobile fix: the backend can return several suggestions and
            // this was previously unbounded — on a short phone viewport
            // (e.g. input near the bottom of a form) the list could run
            // off the bottom edge with no way to scroll to the rest or
            // reach the "type manually" hint below it. Cap + scroll.
            maxHeight: 280,
            overflowY: 'auto',
          }}>
            {suggestions.map((s, i) => (
              <button
                key={i}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); handleSelect(s); }}
                className="w-full text-left"
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  padding: '10px 12px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: i < suggestions.length - 1 ? '1px solid var(--border)' : 'none',
                  cursor: 'pointer', transition: 'background 0.1s',
                  WebkitTapHighlightColor: 'transparent',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--ice-a8)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <MapPin size={14} style={{ color: 'var(--ice)', flexShrink: 0, marginTop: 2 }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {s.name}
                  </div>
                  {s.address && (
                    <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.address}
                    </div>
                  )}
                </div>
              </button>
            ))}
            <div style={{ padding: '4px 12px 6px', borderTop: '1px solid var(--border)' }}>
              <span style={{ fontSize: 9, color: 'var(--text-3)' }}>Powered by OpenStreetMap · Can't find it? Type address manually below.</span>
            </div>
          </div>
        )}
      </div>

      {/* Address input (manual override or auto-filled) */}
      <input
        type="text"
        className="input-field mt-2"
        placeholder="Address (auto-filled or enter manually)"
        value={address}
        disabled={disabled}
        onChange={e => onChangeAddress?.(e.target.value)}
        autoComplete="street-address"
      />
    </div>
  );
}
